<template>
  <div>
    <right-popup
      v-if="!isDestroyed"
      :show="isShow"
      :border-left="false"
      :border-top="true"
      :width="300"
      :exclude-header="true"
      :exclude-footer="true"
      :custom-styles="{zIndex: 9}"
      portal-name="portal-columns-sidebar"
    >
      <template #body>
        <transition
          name="slide-right-left"
          mode="out-in"
        >
          <component
            ref="currentComponent"
            :is="viewConfig.view"
            v-if="viewConfig.view"
            :key="viewConfig.view"
            v-click-outside-mousedown="onClickOutside"
            :view-config="viewConfig"
            @onClose="closeClick"
            @backClick="backClick"
            @onCreateNewColumnClick="onCreateNewColumnClick"
            @onUseExistedColumnClick="onUseExistedColumnClick"
            @openEditBar="openEditBar"
            @onDeleteClick="onDeleteClick"
            @changeView="changeView"
            @helpIconClick="showHelpPopup"
            @changeCheckbox="changeCheckbox"
            @updateConfig="updateConfig"
            @saveNewColumn="saveNewColumn"
            @onReuseExistingColumn="onReuseExistingColumn"
            @redirectToSettings="redirectToSettings"
            @changeCheckboxOption="changeCheckboxOption"
            @setUserChangesFlag="disableGanttAndViewsByUserChanges"
            @showChangesConfirmation="showChangesConfirmation"
            @click.stop
          />
        </transition>
      </template>
    </right-popup>
    <core-confirm-popup
      :show="isShowRemoveConfirmation"
      :popup-width="440"
      button-width="130px"
      close-icon
      :title="locales('columns_bar_remove_confirm_title')"
      :text="locales('columns_bar_remove_confirm_text')"
      :cancel-button="{ title: locales('common_cancel'), type: 'secondary' }"
      :ok-button="{ title: locales('common_remove'), type: 'destructive' }"
      @onClickCancel="cancelDeletion"
      @onClickOk="deleteFromPopup"
    />
    <core-confirm-popup
      :show="isShowChangesConfirmation"
      :popup-width="440"
      button-width="130px"
      close-icon
      :title="locales('columns_bar_changes_confirm_title')"
      :text="locales('custom_column_changes_confirm_message')"
      :cancel-button="{ title: locales('common_cancel'), type: 'secondary' }"
      :ok-button="{ title: locales('common_ok'), type: 'primary' }"
      @onClickCancel="cancelUserChanges"
      @onClickOk="saveUserChanges"
    />
    <core-confirm-popup
      :show="isShowUnsavedConfirmation"
      :popup-width="440"
      button-width="130px"
      close-icon
      :title="locales('columns_bar_unsaved_changes_confirm_title')"
      :text="locales('columns_bar_unsaved_changes_confirm_message')"
      :cancel-button="{ title: locales('common_cancel'), type: 'secondary' }"
      :ok-button="{ title: locales('common_confirm'), type: 'primary' }"
      @onClickCancel="cancelAction"
      @onClickOk="confirmAction"
    />
  </div>
</template>

<script>
import app from '$$app';
import ColumnsBar from './columnsBar';
import rightPopup from '../common/VueComponents/popups/rightPopup/rightPopup.vue';
import columns from './views/columns.vue';
import adding from './views/adding.vue';
import editing from './views/editing.vue';
import coreConfirmPopup from '../common/VueComponents/popups/coreConfirmPopup/coreConfirmPopup.vue';
import helpPopup from '../help/helpPopup';
import helpers from './helpers';
import routerHelper from '$$routerHelper';
import projectsModel from '$$projectModel';

const OUTSIDE_SELECTORS = ['.core_popup', '.help_block', '.core_confirm_popup', '.header_columns_sidebar_button', '.gantt_grid_head_buttons', '.global-popup'];

export default {
  name: 'ColumnsBar',
  components: {
    rightPopup, columns, adding, editing, coreConfirmPopup,
  },
  data() {
    return {
      locales: __,
      appEvents: [],
      isShow: false,
      isDestroyed: true,
      routeType: '',
      projectId: null,
      viewConfig: {
        view: '',
        activeTab: '',
        columns: [],
        columnId: null,
        editable: true,
        currencyEditing: false,
        focusedColumnId: null,
        isArchivedProject: false,
      },
      deletionColumnId: null,
      userUnsavedChanges: null,
      hasUserChanges: false,
      userAction: {},
      isShowRemoveConfirmation: false,
      isShowChangesConfirmation: false,
      isShowUnsavedConfirmation: false,
    };
  },
  computed: {
    isActiveHeaderButton() {
      return this.$store.getters['headerView/isColumnsSidebarOpened'];
    },
  },
  mounted() {
    this.initAppListeners();
    this.initByRoute(this.$route);
  },
  created() {
    this.initRouterListener();
  },
  beforeUnmount() {
    this.appEvents.forEach(event => app.off(event));
    this.appEvents = [];
  },
  methods: {
    initRouterListener() {
      let timeout = null;

      if (timeout) {
        clearTimeout(timeout);
      }

      this.$router.beforeEach((to, from, next) => {
        if (this.hasUserChanges) {
          this.userAction.name = 'changeRoute';
          this.userAction.params = { route: to };
          this.isShowUnsavedConfirmation = true;

          return;
        }

        this.onClose();
        timeout = setTimeout(() => {
          this.initByRoute(to);
          next();
        }, 0);
      });
    },
    initByRoute(route) {
      const projectListRoute = ['projectsMultiView', 'project'].includes(route.name) && route.path.includes('/list');
      const projectGanttRoute = ['projectsMultiView', 'project'].includes(route.name) && route.path.includes('/gantt');
      const listViewRoute = route.name === 'listView';
      const boardRoute = ['project'].includes(route.name) && route.path.includes('/board');

      if (!projectGanttRoute && !projectListRoute && !listViewRoute && !boardRoute) {
        if (route.name !== 'app') {
          this.isDestroyed = true;
        }

        return;
      }

      let canEditByRoute = true;

      if (projectListRoute || listViewRoute) {
        this.routeType = 'list';
      }

      if (projectGanttRoute) {
        this.routeType = 'gantt';
      }

      if (projectGanttRoute || projectGanttRoute) {
        this.projectId = route.params?.projectId;
        const projectData = projectsModel.getProjectDataById(route.params?.projectId);

        if (projectData) {
          this.viewConfig.isArchivedProject = !!projectData.is_archived || false;
        }
      }

      if (['projectsMultiView', 'listView'].includes(route.name)) {
        this.projectId = null;
        canEditByRoute = false;
      }

      this.viewConfig.editable = canEditByRoute;
      this.isDestroyed = false;

      setTimeout(() => {
        this.setColumns();
      }, 0);
    },
    setColumns() {
      if (this.routeType && this.routeType === 'list') {
        this.viewConfig.columns = ColumnsBar.listViewColumns;
      } else {
        this.viewConfig.columns = ColumnsBar.ganttColumns;
      }
    },
    initAppListeners() {
      const openColumnsBar = app.on('columnsBar:open', payload => {
        this.openBar(payload);
      });

      const deleteColumnEvent = app.on('columnsBar:deleteColumn', payload => {
        const { isBoard, columnId } = payload;

        this.onDeleteClick({ id: columnId });
      });

      const userCustomColumnsChange = app.on('userCustomColumnsModel:change', (projectId, action, columnId) => {
        this.setColumns();

        if (this.isShow) {
          if (['create', 'update'].includes(action) && columnId) {
            this.viewConfig.focusedColumnId = columnId;
          }
        }
      });

      const checkboxOptionsChanged = app.on('columnsBar:checkboxOptions:change', () => {
        if (this.isShow) {
          this.setColumns();
        }
      });

      const ganttViewRefresh = app.on('columnsBar:gantt:refresh', () => {
        this.viewConfig.columns = ColumnsBar.ganttColumns;
      });

      const listViewRefresh = app.on('columnsBar:listView:refresh', () => {
        this.setColumns();
      });

      const customColumnsReorderByDnd = app.on('customGrid:reorder', () => {
        ColumnsBar.reorderCustomColumns();
        this.viewConfig.columns = ColumnsBar.ganttColumns;
      });

      const projectArchived = app.on('project:archive', (projectId, isArchived) => {
        if (this.projectId && +this.projectId === +projectId) {
          this.viewConfig.isArchivedProject = isArchived;
        }
      });

      const closeColumnFromGridHeader = app.on('closeColumnFromGridHeader', columnId => {
        ColumnsBar.disableColumnById(columnId);
        this.viewConfig.columns = ColumnsBar.ganttColumns;
      });

      this.appEvents.push(
        openColumnsBar,
        userCustomColumnsChange,
        checkboxOptionsChanged,
        ganttViewRefresh,
        listViewRefresh,
        customColumnsReorderByDnd,
        projectArchived,
        closeColumnFromGridHeader,
        deleteColumnEvent,
      );
    },
    openBar(payload) {
      const {
        view, activeTab, target, columnId, isBoard,
      } = payload;

      if (this.$store.getters['massChange/isEnabled']) {
        this.$store.commit('massChange/setIsEnabled', false);
      }

      const columnsIconGridButton = document.documentElement.querySelector('.gantt_header_custom_grid_icon');

      if (this.isShow) {
        if (this.isActiveHeaderButton && target === 'header') {
          this.onClose();

          return;
        }

        if (columnsIconGridButton && columnsIconGridButton.classList.contains('columns_sidebar_opened') && target === 'grid') {
          this.onClose();

          return;
        }

        if (target === 'header') {
          this.$store.commit('headerView/toggleColumnsButton', true);
          columnsIconGridButton && columnsIconGridButton.classList.remove('columns_sidebar_opened');
        }

        if (target === 'grid') {
          this.$store.commit('headerView/toggleColumnsButton', false);
          columnsIconGridButton && columnsIconGridButton.classList.add('columns_sidebar_opened');
        }
      }

      setTimeout(() => {
        if (columnId) {
          this.viewConfig = {
            ...this.viewConfig,
            columnId,
            isBoard,
          };
        }

        this.changeView(view, activeTab);
        this.isShow = true;

        if (target === 'header') {
          userExtAnalytics.log('gantt_column_settings_open', {
            from: this.$route?.name === 'projectsMultiView' ? 'portfolio' : 'project',
            button: 'header',
          });
          this.$store.commit('headerView/toggleColumnsButton', true);
        }

        if (target === 'grid') {
          columnsIconGridButton && columnsIconGridButton.classList.add('columns_sidebar_opened');
        }
      }, 100);
    },
    changeView(view, activeTab) {
      const targetView = view || this.viewConfig.view;
      const targetTab = activeTab || this.viewConfig.activeTab;

      if (this.viewConfig.activeTab === 'customColumns') {
        this.viewConfig.focusedColumnId = null;
      }

      this.viewConfig = {
        ...this.viewConfig,
        view: targetView,
        activeTab: targetTab,
      };
    },
    updateConfig(payload) {
      this.viewConfig = {
        ...this.viewConfig,
        ...payload,
      };
    },
    closeClick() {
      if (this.hasUserChanges) {
        this.userAction.name = 'close';
        this.isShowUnsavedConfirmation = true;

        return;
      }

      this.onClose();
    },
    onClose() {
      const columnsIconGridButton = document.documentElement.querySelector('.gantt_header_custom_grid_icon');

      columnsIconGridButton && columnsIconGridButton.classList.remove('columns_sidebar_opened');
      this.$store.commit('headerView/toggleColumnsButton', false);

      this.isShow = false;

      setTimeout(() => {
        this.viewConfig = {
          ...this.viewConfig,
          view: 'columns',
          activeTab: 'customColumns',
          columnId: null,
          currencyEditing: false,
          focusedColumnId: null,
          isBoard: false,
        };
      }, 100);

      this.deletionColumnId = null;
      this.userUnsavedChanges = null;
      this.userAction = {};
      this.hasUserChanges = false;
      this.isShowChangesConfirmation = false;
    },
    backClick() {
      if (this.hasUserChanges) {
        this.userAction.name = 'back';
        this.isShowUnsavedConfirmation = true;

        return;
      }

      this.onBack();
    },
    onBack() {
      const targetTab = this.viewConfig.currencyEditing ? 'standartColumns' : 'customColumns';

      this.changeView('columns', targetTab);
    },
    onClickOutside(e) {
      if (document.querySelector('[data-tippy-root]')) return;
      if (this.$refs.currentComponent.$el.querySelector('.smooth-dnd-ghost.list__item')) return; // don't proceed if drag-n-drop is in progress
      if (this.isShowUnsavedConfirmation) return;
      if (this.hasUserChanges) {
        this.userAction.name = 'clickOutside';
        this.isShowUnsavedConfirmation = true;

        return;
      }
      if (this.isShowChangesConfirmation) return;
      if (this.isShowRemoveConfirmation) return;
      if (OUTSIDE_SELECTORS.some(selector => e.target.closest(`${selector}`))) return;
      if (this.isShow) {
        setTimeout(() => {
          this.onClose();
        }, 0);
      }
    },
    onCreateNewColumnClick() {
      this.changeView('adding', 'createColumns');
    },
    onUseExistedColumnClick() {
      this.changeView('adding', 'existingColumns');
    },
    openEditBar(column) {
      if (column.currency) {
        this.viewConfig.currencyEditing = true;
        this.changeView('editing', null);
      } else {
        this.viewConfig.currencyEditing = false;
        this.viewConfig.columnId = column.id;
        this.changeView('editing', null);
      }
    },
    onDeleteClick(column) {
      this.isShowRemoveConfirmation = true;
      this.deletionColumnId = column.id;
    },
    deleteFromPopup() {
      setTimeout(() => { // delay for clickOutside
        userExtAnalytics.log('columns_sidebar_confirm_remove_click');
        const storeData = this.$store.getters['columns/getCustomColumnById'](this.deletionColumnId);
        const deletionData = helpers.prepareDataToDelete(storeData);

        this.$store.dispatch('columns/updateColumn', { column: deletionData, isShow: false });
        this.isShowRemoveConfirmation = false;
        this.deletionColumnId = null;
      }, 0);
    },
    cancelDeletion() {
      setTimeout(() => { // delay for clickOutside
        this.isShowRemoveConfirmation = false;
        this.deletionColumnId = null;
      }, 0);
    },
    showHelpPopup({ event, id }) {
      this.$store.dispatch('user/updateActivityStatus', { activityName: 'customGridHelpBlock', status: 1 });
      helpPopup.showHelpBlock(event, id);
    },
    changeCheckbox(payload) {
      ColumnsBar.changeCheckbox(payload, this.routeType);

      if (this.routeType === 'gantt') {
        this.viewConfig.columns = ColumnsBar.ganttColumns;
      }

      if (this.routeType === 'list') {
        this.viewConfig.columns = ColumnsBar.listViewColumns;
      }

      if (payload.value) {
        this.viewConfig.focusedColumnId = +payload.id;
      }
    },
    changeCheckboxOption(payload) {
      ColumnsBar.changeOptionCheckbox(payload, this.routeType);

      if (this.routeType === 'gantt') {
        this.viewConfig.columns = ColumnsBar.ganttColumns;
      }

      if (this.routeType === 'list') {
        this.viewConfig.columns = ColumnsBar.listViewColumns;
      }
    },
    disableGanttAndViewsByUserChanges(flag) {
      this.hasUserChanges = flag;
      this.$store.commit('setColumnsSidebarConfirmationStatus', flag);
    },
    showChangesConfirmation(data) {
      this.userUnsavedChanges = data;
      this.disableGanttAndViewsByUserChanges(false);
      this.isShowChangesConfirmation = true;
    },
    saveUserChanges() {
      setTimeout(() => { // delay for clickOutside
        const updatedData = {
          ...this.userUnsavedChanges,
          id: this.viewConfig.columnId,
        };

        this.$store.dispatch('columns/updateColumn', { column: updatedData, isShow: false });

        if (this.viewConfig.isBoard) {
          this.onClose();

          return;
        }

        this.changeView('columns', 'customColumns');
        this.userUnsavedChanges = null;
        this.isShowChangesConfirmation = false;
      }, 0);
    },
    cancelUserChanges() {
      setTimeout(() => { // delay for clickOutside
        this.userUnsavedChanges = null;
        this.disableGanttAndViewsByUserChanges(true);
        this.isShowChangesConfirmation = false;
      }, 0);
    },
    async saveNewColumn(data) {
      const newData = helpers.prepareNewColumnDataForSave(data);
      const fromList = this.routeType === 'list';

      await this.$store.dispatch('columns/createColumn', {
        column: newData,
        isGlobal: false,
        fromList,
      });

      this.changeView('columns', 'customColumns');
      this.disableGanttAndViewsByUserChanges(false);
    },
    async onReuseExistingColumn(columnId) {
      const updatedData = helpers.prepareDataForReuseColumn(columnId);
      const fromList = this.routeType === 'list';

      this.disableGanttAndViewsByUserChanges(false);

      await this.$store.dispatch('columns/updateColumn', {
        column: updatedData,
        isShow: !fromList,
        fromList,
      });

      this.changeView('columns', 'customColumns');
    },
    redirectToSettings() {
      routerHelper.changeRoute('/settings/columns');
    },
    confirmAction() {
      setTimeout(() => { // delay for clickOutside
        this.callUserActions();
        this.disableGanttAndViewsByUserChanges(false);
        this.isShowUnsavedConfirmation = false;
      });
    },
    cancelAction() {
      setTimeout(() => { // delay for clickOutside
        this.userAction = {};
        this.isShowUnsavedConfirmation = false;
      }, 0);
    },
    callUserActions() {
      if (this.userAction?.name) {
        if (this.userAction.name === 'changeRoute') {
          const newRoute = this.userAction.params.route.fullPath;

          this.onClose();
          setTimeout(() => {
            this.$router.replace(newRoute);
          }, 500);
        }

        if (['clickOutside', 'close'].includes(this.userAction.name)) {
          this.onClose();
        }

        if (this.userAction.name === 'back') {
          this.onBack();
        }

        this.userAction = {};
      }
    },
  },
};
</script>

<style src='./components/tooltips/style.less' lang='less'></style>
